import '@fontsource/poppins/300.css';
import '@fontsource/poppins/400-italic.css';
import '@fontsource/poppins/400.css';
import '@fontsource/poppins/500.css';
import '@fontsource/poppins/700.css';
import './styles/styles.css';

import {
  AutoLogoutProvider,
  LicenseGuard,
  SignInPage,
  installLicenseExpiredInterceptor,
} from '@agilelab/plugin-wb-auth';
import {
  CatalogEntityPage,
  CatalogGraphRenderNode,
  CatalogIndexPage,
  AdministratorCatalogIndexPage,
  catalogPlugin,
  MyProjectsPage,
  ReleaseDetailPage,
} from '@agilelab/plugin-wb-builder-catalog';
import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@agilelab/plugin-wb-catalog-import';
import { eventsApiRef } from '@agilelab/plugin-wb-events';
import { userSignedInTopic } from '@agilelab/plugin-wb-events-common';
import {
  WbExtensionManagerPage,
  wbExtensionManagerPlugin,
} from '@agilelab/plugin-wb-extension-manager';
import {
  GovernancePage,
  governancePlugin,
} from '@agilelab/plugin-wb-governance';
import {
  MarketplacePage,
  marketplacePlugin,
} from '@agilelab/plugin-wb-marketplace';
import {
  NotificationPage,
  NotificationProvider,
} from '@agilelab/plugin-wb-notification';
import {
  CustomAlertDisplay,
  EnabledFeaturesProvider,
  EnvironmentsContextProvider,
  LightAgileTheme,
  MeshApolloProvider,
  MobileProvider,
  useCustomTheme,
  WbHomeRedirect,
  WbRequirePermission,
} from '@agilelab/plugin-wb-platform';
import {
  PlatformSettingsPage,
  platformSettingsPlugin,
} from '@agilelab/plugin-wb-platform-settings';
import { WITBOOST_RELATION_PAIRS } from '@agilelab/plugin-wb-practice-shaper-common';
import { RbacPanel } from '@agilelab/plugin-wb-rbac';
import {
  ArrayTableTemplateLayout,
  BlueprintsPage,
  HorizontalTemplateLayout,
  ScaffolderLayouts,
  ScaffolderPage,
  scaffolderPlugin,
  TableRowTemplateLayout,
  TemplatesPage,
  WittyTableRowTemplateLayout,
  WittyTableTemplateLayout,
} from '@agilelab/plugin-wb-scaffolder';
import { WbSystemPrototypePage } from '@agilelab/plugin-wb-system-prototype';
import {
  DocumentsViewerTab,
  MyFeatureRequestsPanel,
  UserSettingsPage,
  UserSettingsTab,
} from '@agilelab/plugin-wb-user-settings';
import { createApp } from '@mainstage/app-defaults';
import {
  RELATION_DEPENDENCY_OF,
  RELATION_DEPENDS_ON,
  RELATION_HAS_PART,
  RELATION_PART_OF,
} from '@mainstage/catalog-model';
import { AppRouter, FlatRoutes } from '@mainstage/core-app-api';
import { ErrorPage, OAuthRequestDialog } from '@mainstage/core-components';
import { configApiRef, IdentityApi, useApi } from '@mainstage/core-plugin-api';
import { catalogEntityCreatePermission } from '@mainstage/plugin-catalog-common/alpha';
import {
  CatalogGraphPage,
  catalogGraphPlugin,
} from '@mainstage/plugin-catalog-graph';
import { orgPlugin } from '@mainstage/plugin-org';
import { RequirePermission } from '@mainstage/plugin-permission-react';
import {
  techdocsPlugin,
  TechDocsReaderPage,
  TechDocsReaderPageContent,
} from '@agilelab/plugin-wb-techdocs';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { PropsWithChildren, useMemo } from 'react';
import { Route } from 'react-router';
import { apis } from './apis';
import { entityPage } from './components/catalog/EntityPage';
import { DocumentsPopup } from './components/documents/DocumentsPopup';
import { Root } from './components/Root';
import { UserConfigSettings } from './components/settings/UserConfigSettings';
import { UserHeadersSettings } from './components/settings/UserHeadersSettings/UserHeadersSettings';
import { UserAccessTokenSettings } from './components/settings/UserAccessTokenSettings';
import { UserMcpOAuthSettings } from './components/settings/UserMcpOAuthSettings';
import { CommercialGate } from './components/common/CommercialGate';
import {
  getUserConfigsRequiredModules,
  getUserHeadersRequiredModules,
  getUserAccessTokensRequiredModules,
  getAdvancedRequiredModules,
} from './components/settings/requiredModules';
import { accessControlPlugin } from '@agilelab/plugin-wb-access-control';
import {
  AgentContext,
  WittyAssistantProvider,
} from '@agilelab/plugin-wb-witty-react';

const queryClient = new QueryClient({
  // Disable refetch on window focus globally to avoid too many requests to backend.
  // It can be re-enabled on specific queries if needed
  defaultOptions: { queries: { refetchOnWindowFocus: false } },
});

const scaffolderLayouts = () => {
  return (
    <ScaffolderLayouts>
      <TableRowTemplateLayout />
      <WittyTableRowTemplateLayout />
      <ArrayTableTemplateLayout />
      <WittyTableTemplateLayout />
      <HorizontalTemplateLayout />
    </ScaffolderLayouts>
  );
};

const AdminMenusRouteGate = ({ children }: PropsWithChildren<{}>) => {
  const configApi = useApi(configApiRef);
  const adminMenusEnabled =
    configApi.getOptionalBoolean(
      'mesh.platformSettings.ui.experimental.adminMenusEnabled',
    ) ?? true;

  if (!adminMenusEnabled) {
    return <ErrorPage status="404" statusMessage="PAGE NOT FOUND" />;
  }

  return <>{children}</>;
};

const SignInComponent: any = (props: any) => {
  const eventsApi = useApi(eventsApiRef);
  // The CustomAlertDisplay component is used to display alerts in the sign in page (e.g. when the user is not authorized).
  // You can see that inside the AppRouter, another CustomAlertDisplay component is used to display alerts in the main app.
  // This is because the sign in page does not have a Router, so links inside the toasts will fail: for this reason we need to have two separate ones.
  // N.B.: the Backstage's AppRouter class will destroy the CustomAlertDisplay component when the login completes, so the two components will not conflict.
  return (
    <>
      <CustomAlertDisplay />
      <SignInPage
        {...props}
        auto
        title="Select a sign-in method"
        align="center"
        onSignInSuccess={async (identityApi: IdentityApi) => {
          const identity = await identityApi.getBackstageIdentity();
          eventsApi.publish(
            userSignedInTopic.createEvent(
              {},
              {
                generatorSystem: 'settings',
                originatorUri: identity.userEntityRef,
              },
            ),
            await identityApi.getCredentials(),
          );
          props.onSignInSuccess(identityApi);
        }}
      />
    </>
  );
};

function routes() {
  return (
    <FlatRoutes>
      <Route path="/" element={<WbHomeRedirect />} />
      {/* TODO: check if this page is needed after moved software catalog to admin page */}
      <Route
        path="/catalog"
        element={
          <AgentContext description="Builder catalog of platform entities">
            <WbRequirePermission
              when={ctx => ctx.catalogEnabled && ctx.softwareCatalogEnabled}
            >
              <CatalogIndexPage />
            </WbRequirePermission>
          </AgentContext>
        }
      />
      <Route
        path="/platform-settings/catalog"
        element={
          <AgentContext description="Administration catalog of platform entities">
            <WbRequirePermission
              when={ctx =>
                ctx.platformSettingsEnabled &&
                ctx.softwareCatalogEnabled &&
                ctx.catalogEnabled
              }
            >
              <AdministratorCatalogIndexPage />
            </WbRequirePermission>
          </AgentContext>
        }
      />
      <Route
        path="/platform-settings/catalog/:namespace/:kind/:name"
        element={
          <AgentContext description="Administration details for a platform entity">
            <WbRequirePermission when={ctx => ctx.platformSettingsEnabled}>
              <CatalogEntityPage />
            </WbRequirePermission>
          </AgentContext>
        }
      >
        {entityPage}
      </Route>
      <Route
        path="/my-projects/catalog"
        element={
          <AgentContext description="Builder projects owned by the current user">
            <WbRequirePermission when={ctx => ctx.catalogEnabled}>
              <MyProjectsPage />
            </WbRequirePermission>
          </AgentContext>
        }
      />
      <Route
        path="/my-projects/catalog/:namespace/:kind/:name"
        element={
          <AgentContext description="Builder details for a project entity">
            <WbRequirePermission when={ctx => ctx.catalogEnabled}>
              <CatalogEntityPage />
            </WbRequirePermission>
          </AgentContext>
        }
      >
        {entityPage}
      </Route>
      <Route
        path="/catalog/release/:kind/:namespace/:name"
        element={
          <AgentContext
            description="Builder release details for a project"
            environmentScoped
          >
            <WbRequirePermission when={ctx => ctx.catalogEnabled}>
              <ReleaseDetailPage />
            </WbRequirePermission>
          </AgentContext>
        }
      />
      <Route
        path="/catalog/:namespace/:kind/:name"
        element={
          <AgentContext description="Builder details for a platform entity">
            <WbRequirePermission when={ctx => ctx.catalogEnabled}>
              <CatalogEntityPage />
            </WbRequirePermission>
          </AgentContext>
        }
      >
        {entityPage}
      </Route>
      <Route
        path="/catalog-graph"
        element={
          <AgentContext description="Graph of systems, components, and their relationships">
            <WbRequirePermission when={ctx => ctx.catalogEnabled}>
              <CatalogGraphPage
                renderNode={CatalogGraphRenderNode}
                initialState={{
                  selectedKinds: ['component', 'system'],
                  selectedRelations: [
                    RELATION_HAS_PART,
                    RELATION_PART_OF,
                    RELATION_DEPENDS_ON,
                    RELATION_DEPENDENCY_OF,
                  ],
                }}
                relationPairs={WITBOOST_RELATION_PAIRS}
              />
            </WbRequirePermission>
          </AgentContext>
        }
      />
      <Route
        path="/docs/:namespace/:kind/:name/*"
        element={
          <AgentContext description="Technical documentation for a platform entity">
            <TechDocsReaderPage>
              <TechDocsReaderPageContent />
            </TechDocsReaderPage>
          </AgentContext>
        }
      />
      <Route
        path="/templates"
        element={
          <AgentContext description="Builder templates for creating systems and components">
            <WbRequirePermission when={ctx => ctx.templatesEnabled}>
              <TemplatesPage />
            </WbRequirePermission>
          </AgentContext>
        }
      />
      <Route
        path="/blueprints"
        element={
          <AgentContext description="Builder blueprints for creating complete projects">
            <WbRequirePermission when={ctx => ctx.blueprintsEnabled}>
              <BlueprintsPage />
            </WbRequirePermission>
          </AgentContext>
        }
      >
        {scaffolderLayouts()}
      </Route>
      <Route
        path="/platform-settings/catalog-import"
        element={
          <AgentContext description="Administration import of existing catalog entities">
            <RequirePermission permission={catalogEntityCreatePermission}>
              <CatalogImportPage />
            </RequirePermission>
          </AgentContext>
        }
      />
      <Route
        path="/settings"
        element={
          <AgentContext description="Personal settings for the current user">
            <UserSettingsPage />
          </AgentContext>
        }
      >
        <UserSettingsTab
          path="/advanced"
          title="Advanced"
          requiredModules={getAdvancedRequiredModules()}
        >
          <CommercialGate required={getUserConfigsRequiredModules()}>
            <UserConfigSettings />
          </CommercialGate>
          <UserMcpOAuthSettings />
          <CommercialGate required={getUserHeadersRequiredModules()}>
            <UserHeadersSettings />
          </CommercialGate>
          <CommercialGate required={getUserAccessTokensRequiredModules()}>
            <UserAccessTokenSettings />
          </CommercialGate>
        </UserSettingsTab>
        <UserSettingsTab path="documents-table" title="Documents">
          <DocumentsViewerTab />
        </UserSettingsTab>
        <UserSettingsTab path="feature-requests" title="Feature Requests">
          <MyFeatureRequestsPanel />
        </UserSettingsTab>
      </Route>
      <Route
        path="/marketplace"
        element={
          <AgentContext description="Marketplace for discovering published products and consumables">
            <WbRequirePermission when={ctx => ctx.marketplaceEnabled}>
              <MarketplacePage />
            </WbRequirePermission>
          </AgentContext>
        }
      />
      <Route
        path="/scaffolder"
        element={
          <AgentContext description="Builder workflow for creating systems and components from templates">
            <WbRequirePermission when={ctx => ctx.templatesEnabled}>
              <ScaffolderPage />
            </WbRequirePermission>
          </AgentContext>
        }
      >
        {scaffolderLayouts()}
      </Route>
      <Route
        path="/governance"
        element={
          <AgentContext description="Computational Governance for policies, metrics, resources, and evaluations">
            <WbRequirePermission when={ctx => ctx.governanceEnabled}>
              <GovernancePage />
            </WbRequirePermission>
          </AgentContext>
        }
      />
      <Route
        path="/notifications"
        element={
          <AgentContext description="Notifications for relevant platform events">
            <NotificationPage />
          </AgentContext>
        }
      />
      <Route
        path="/platform-settings"
        element={
          <AgentContext description="Platform administration and configuration">
            <WbRequirePermission when={ctx => ctx.platformSettingsEnabled}>
              <PlatformSettingsPage />
            </WbRequirePermission>
          </AgentContext>
        }
      >
        {/* pass custom layouts so they can be accessed by template studio under
        this route */}
        {scaffolderLayouts()}
      </Route>
      <Route
        path="/platform-settings/rbac"
        element={
          <AgentContext description="Administration of roles and permissions">
            <WbRequirePermission when={ctx => ctx.platformSettingsEnabled}>
              <RbacPanel />
            </WbRequirePermission>
          </AgentContext>
        }
      />
      <Route
        path="/platform-settings/extension-manager"
        element={
          <AgentContext description="Administration of platform extensions and integrations">
            <AdminMenusRouteGate>
              <WbRequirePermission
                when={ctx => ctx.extensionManagerSectionEnabled}
              >
                <WbExtensionManagerPage />
              </WbRequirePermission>
            </AdminMenusRouteGate>
          </AgentContext>
        }
      />
      <Route
        path="/prototypes"
        element={
          <AgentContext description="Early design and validation of system prototypes">
            <WbRequirePermission when={ctx => ctx.prototypePageEnabled}>
              <WbSystemPrototypePage />
            </WbRequirePermission>
          </AgentContext>
        }
      >
        {scaffolderLayouts()}
      </Route>
    </FlatRoutes>
  );
}

// Intercept all fetch responses to detect license expiration globally,
// before any React component can process the error.
installLicenseExpiredInterceptor();

const WitboostApp = () => {
  const { error, loading, theme } = useCustomTheme();
  const themes = useMemo(() => {
    if (theme && !loading && !error) return [theme];
    return [LightAgileTheme];
  }, [error, loading, theme]);

  if (loading) return <></>;

  const app = createApp({
    apis,
    themes: themes,
    components: {
      SignInPage: SignInComponent,
    },
    bindRoutes({ bind }) {
      bind(catalogPlugin.externalRoutes, {
        registerComponent: catalogImportPlugin.routes.importPage,
        createComponent: scaffolderPlugin.routes.root,
        viewTechDoc: techdocsPlugin.routes.docRoot,
        templatesRootRouteRef: scaffolderPlugin.routes.templateRouteRef,
        blueprintsRootRouteRef: scaffolderPlugin.routes.blueprintRouteRef,
        templateIndexRouteRef:
          platformSettingsPlugin.routes.templateIndexRouteRef,
        taskListRouteRef: platformSettingsPlugin.routes.taskListRouteRef,
        templateEditorRouteRef:
          platformSettingsPlugin.routes.templateEditorRouteRef,
        actionsRouteRef: platformSettingsPlugin.routes.actionsRouteRef,
        taskRouteRef: platformSettingsPlugin.routes.taskRouteRef,
        iamRouteRef: platformSettingsPlugin.routes.iamRouteRef,
      });
      bind(catalogGraphPlugin.externalRoutes, {
        catalogEntity: catalogPlugin.routes.catalogEntity,
      });
      bind(scaffolderPlugin.externalRoutes, {
        registerComponent: catalogImportPlugin.routes.importPage,
        templateIndexRouteRef:
          platformSettingsPlugin.routes.templateIndexRouteRef,
        actionsRouteRef: platformSettingsPlugin.routes.actionsRouteRef,
        templateEditorRouteRef:
          platformSettingsPlugin.routes.templateEditorRouteRef,
        taskListRouteRef: platformSettingsPlugin.routes.taskListRouteRef,
        skeletonRenderingPreviewRouteRef:
          platformSettingsPlugin.routes
            .builderToolsSkeletonRenderingPreviewRouteRef,
      });
      bind(catalogImportPlugin.externalRoutes, {
        templates: scaffolderPlugin.routes.root,
        catalog: catalogPlugin.routes.catalogIndex,
        adminCatalogRouteRef: catalogPlugin.routes.adminCatalogRouteRef,
      });
      bind(orgPlugin.externalRoutes, {
        catalogIndex: catalogPlugin.routes.catalogIndex,
      });
      bind(wbExtensionManagerPlugin.externalRoutes, {
        policyPage: governancePlugin.routes.testPolicyPage,
      });
      bind(accessControlPlugin.externalRoutes, {
        marketplaceProductRef: marketplacePlugin.routes.marketplaceProductRef,
      });
    },
  });

  const AppRoot = app.createRoot(
    <>
      <MobileProvider>
        <DocumentsPopup />
        <OAuthRequestDialog />
        <AppRouter>
          <CustomAlertDisplay />
          <LicenseGuard>
            <MeshApolloProvider>
              <QueryClientProvider client={queryClient}>
                <NotificationProvider>
                  <AutoLogoutProvider>
                    <EnvironmentsContextProvider>
                      <EnabledFeaturesProvider>
                        <WittyAssistantProvider>
                          <Root>{routes()}</Root>
                        </WittyAssistantProvider>
                      </EnabledFeaturesProvider>
                    </EnvironmentsContextProvider>
                  </AutoLogoutProvider>
                </NotificationProvider>
              </QueryClientProvider>
            </MeshApolloProvider>
          </LicenseGuard>
        </AppRouter>
      </MobileProvider>
    </>,
  );

  return <AppRoot />;
};

export default WitboostApp;
